import Box from '@/Atoms/Box';
import CCLoader from '@/Atoms/CCLoader';
import CCPackageTable from '@/Atoms/CCPackageTable';
import CCText from '@/Atoms/CCText';
import Spacer from '@/Atoms/Spacer';
import useGetSubscriptionDetailsApi from '@/Hooks/SubscriptionDetails/useSubscriptionDetails';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';
import Pagination from '@/Screens/Games/Pagination';

import CCColors from '@/Utils/CCColors';
import {FontSizes, vs} from '@/Utils/Dimensions';
import React, {useEffect, useState} from 'react';
import {Image, Linking, ScrollView} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import styles from './styles';
import CCButton from '@/Atoms/CCButton';
import EmptyView from '../EmptyView';
import useGetRenewalForUser from '@/Hooks/RenewalApi/useGetRenewalForUser';
import RenewalBanner from '@/Screens/Renewal/components/RenewalBanner';
import { openInNewTab } from '@/Utils/CommonUtils';
import SlideModal from '@/Atoms/SlideModal';
import CCDatePicker from '@/Atoms/CCDatePicker';
import usePauseCsocSubscription from '@/Hooks/SubscriptionApi/usePauseCsocSubscription';
import useResumeCsocSubscription from '@/Hooks/SubscriptionApi/useResumeCsocSubscription';
import useGetCsocPauseLimits from '@/Hooks/SubscriptionApi/useGetCsocPauseLimits';
import useAlertModalStore from '@/Store/AlertModalStore';
import ChipList from '@/Atoms/ChipList/ChipList';
import CCSelect from '@/Atoms/CCSelect';

const tabHeaders = [
  'Order ID',
  'Player ID',
  'Mentor',
  'Plan Details',
  'Plan Status',
  'Start Date',
  'End Date',
  'Date Created',
  'Certificate',
  'Action'
];

const SubscriptionTab = ({userContact}) => {
  const {isMobileOrTab} = useMediaQuery();
  const [subscriptionDetails, setSubscriptionDetails] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState<number>(1);
    const [showRenewalBanner, setShowRenewalBanner] = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [pauseOrderId, setPauseOrderId] = useState('');
  const [pauseEndDate, setPauseEndDate] = useState<Date | null>(null);
  const [pauseError, setPauseError] = useState('');
  const [resumeError, setResumeError] = useState('');
  const [pauseLimits, setPauseLimits] = useState<any>(null);
  const [pauseLimitsError, setPauseLimitsError] = useState('');
  const [selectedWeeks, setSelectedWeeks] = useState(1);
  const openAlertModal = useAlertModalStore(state => state.openModal);
  
   const {data: renewalData, isLoading: isLoadingRenewal} =
      useGetRenewalForUser();
  
    // Show renewal banner if eligible (but hide for international users)
    useEffect(() => {
      if (renewalData?.banner?.should_show) {
        // Check if user is international - hide banner if so
        const mobileNumber = renewalData?.user?.mobile;
        if (mobileNumber)  {
          setShowRenewalBanner(true);
        }
      }
    }, [renewalData]);

    useEffect(() => {
      if(resumeError){
        openAlertModal({
          header: 'Error',
          bodyTxt: resumeError,
          buttonText: 'OK',
          handleConfirmation: () => {
            setResumeError('');
          },
        });
      }
    },[resumeError]);
  const handleOnError = (error: any) => {
    console.log('Error fetching subscription details:', error);
  };
  const handleOnSuccessSubscriptionDetails = (data: any) => {
    console.log('Subscription details = ', data);
    setSubscriptionDetails(data?.csoc_list);
  };
  const {mutate: getSubscriptionDetails} = useGetSubscriptionDetailsApi(
    handleOnSuccessSubscriptionDetails,
    handleOnError,
  );
  const {mutate: pauseSubscription, isLoading: isPausing} =
    usePauseCsocSubscription(
      (data:any) => {
        setShowPauseModal(false);
        setPauseEndDate(null);
        setPauseOrderId('');
        setPauseError('');
        setSelectedWeeks(1);
        getSubscriptionDetails();
      },
      (error: any) => {
        setPauseError(error?.response?.data?.detail || error?.response?.data?.message || error?.message || 'Failed to pause subscription');
      },
    );
  const {mutate: resumeSubscription, isLoading: isResuming} =
    useResumeCsocSubscription(
      () => {
        getSubscriptionDetails();
      },
      (error: any) => {
        setResumeError(error?.response?.data?.detail || error?.response?.data?.message || error?.message || 'Failed to resume subscription');
        // setPauseError(error?.message || 'Failed to resume subscription');
      },
    );
  const {mutate: getPauseLimits, isLoading: isLoadingPauseLimits} =
    useGetCsocPauseLimits(
      data => {
        console.log('Pause limits = ', {...data?.limits,...data});
        setPauseLimits({...data?.limits,...data});
        setPauseLimitsError('');
      },
      (error: any) => {
        setPauseLimitsError(error?.message || 'Failed to load pause limits');
      },
    );
  useEffect(() => {
    getSubscriptionDetails();
  }, []);
  const paginate = (pageNumber: number) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
      getSubscriptionDetails();
    }
  };
  const [tableData, setTableData] = useState<
    Array<
      Array<{
        name: string;
        type: 'text' | 'link' | 'dropdown';
        url?: string;
        options?: string[];
      }>
    >
  >([]);
  function formatDate(dateOnly) {
    // Split into year, month, and day
    const [year, month, day] = dateOnly.split('-');

    // Month names array
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    function getOrdinalSuffix(day) {
      const dayNumber = parseInt(day, 10);
      if (dayNumber >= 11 && dayNumber <= 13) return 'th';
      switch (dayNumber % 10) {
        case 1:
          return 'st';
        case 2:
          return 'nd';
        case 3:
          return 'rd';
        default:
          return 'th';
      }
    }
    // Convert month number to month name
    const monthName = monthNames[parseInt(month, 10) - 1];
    const ordinalSuffix = getOrdinalSuffix(day);
    // Format to dd MMM yy
    return `${day}${ordinalSuffix} ${monthName} '${year.slice(2)}`;
  }

  const formatPauseEndDate = (value: Date | null) => {
    if (!value || isNaN(value.getTime())) {
      return '';
    }
    const day = String(value.getDate()).padStart(2, '0');
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const year = value.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatIsoDate = (value?: string | null) => {
    if (!value) {
      return 'N/A';
    }
    const parsed = new Date(value);
    if (isNaN(parsed.getTime())) {
      return 'N/A';
    }
    return formatPauseEndDate(parsed);
  };

  const remainingPauseDays = Number(pauseLimits?.max_pause_days_remaining || 0);
  const minPauseDate = pauseLimits?.min_pause_end_date
    ? new Date(pauseLimits.min_pause_end_date)
    : new Date();
  const maxPauseDate = pauseLimits
    ? remainingPauseDays > 0 && pauseLimits?.max_pause_end_date
      ? new Date(pauseLimits.max_pause_end_date)
      : minPauseDate
    : null;

  const calculatedPauseEndDate = new Date();
  calculatedPauseEndDate.setDate(calculatedPauseEndDate.getDate() + Math.min(selectedWeeks * 7, remainingPauseDays));

  const maxWeeks = Math.floor(remainingPauseDays / 7);
  const weekOptions = Array.from({ length: maxWeeks }, (_, i) => i + 1).map(weeks => ({
    label: `${weeks} week${weeks > 1 ? 's' : ''}`,
    value: weeks,
  }));

  const handleResumeSubscription = (order_id) => {
    openAlertModal({
      header: 'Confirm',
      bodyTxt: `Are you sure you want to resume this subscription?`,
      buttonText: 'Yes',
      handleConfirmation: () => {
        resumeSubscription({order_id})
      },
    });
  }
  useEffect(() => {
    if (subscriptionDetails && Array.isArray(subscriptionDetails)) {
      const itemsPerPage = 10;
      const totalItems = subscriptionDetails.length;

      setTotalPages(Math.ceil(totalItems / itemsPerPage));
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;

      const paginatedData = subscriptionDetails.slice(startIndex, endIndex);
      const formattedData = paginatedData.map(item => {
        console.log('subscription is .... : ', item);
        const order_id = item?.order_id;
        const player_id = item?.player_id;
        const mentor = item?.mentor;
        const batch_details = item?.batch_details.split(' ')[0];
        const start_date = formatDate(item?.start_date);
        const end_date = formatDate(item?.end_date);
        const status = item?.status;
        const date_created = formatDate(item?.date.split('T')[0]);
        const class_details = item?.class_details;
        const certificate = item?.certificate || null ;
        const normalizedStatus = `${status || ''}`.toLowerCase();


        let itemValues: any = [];
        itemValues.push({
          name: isMobileOrTab ? (
            <Box flexDirection="row" style={styles.tableStyle}>
              <CCText fontSize={FontSizes[15]} fontWeight={600}>
                Order ID:
              </CCText>
              <CCText fontSize={FontSizes[15]}> {order_id}</CCText>
            </Box>
          ) : (
            <Box style={styles.tableStyle}>
              <Box type="row-flex-start" style={styles.rowFlex}>
                <CCText color={CCColors.Grey.Grey} fontSize={FontSizes[13]}>
                  {order_id}
                </CCText>
              </Box>
            </Box>
          ),
          type: 'text',
        });
        itemValues.push({
          name: isMobileOrTab ? (
            <Box flexDirection="row" style={styles.tableStyle}>
              <CCText fontSize={FontSizes[15]} fontWeight={600}>
                Player ID:
              </CCText>
              <CCText fontSize={FontSizes[15]}> {player_id}</CCText>
            </Box>
          ) : (
            <Box style={styles.tableStyle}>
              <Box type="row-flex-start" style={styles.rowFlex}>
                <CCText color={CCColors.Grey.Grey} fontSize={FontSizes[13]}>
                  {player_id}
                </CCText>
              </Box>
            </Box>
          ),
          type: 'text',
        });
        itemValues.push({
          name: isMobileOrTab ? (
            <Box flexDirection="row" style={styles.tableStyle}>
              <CCText fontSize={FontSizes[15]} fontWeight={600}>
                Mentor:
              </CCText>
              <CCText fontSize={FontSizes[15]}> {mentor}</CCText>
            </Box>
          ) : (
            <Box style={styles.tableStyle}>
              <Box type="row-flex-start" style={styles.rowFlex}>
                <CCText color={CCColors.Grey.Grey} fontSize={FontSizes[13]}>
                  {mentor}
                </CCText>
              </Box>
            </Box>
          ),
          type: 'text',
        });
        itemValues.push({
          name: isMobileOrTab ? (
            <Box flexDirection="column" style={styles.tableStyle}>
              <Box flexDirection="row">
                <CCText fontSize={FontSizes[15]} fontWeight={600}>
                  Batch:{' '}
                </CCText>
                <CCText fontSize={FontSizes[15]}>{batch_details}</CCText>
              </Box>
              {class_details !== '' && (
                <Box flexDirection="row">
                  <CCText fontSize={FontSizes[15]} fontWeight={600}>
                    Class:{' '}
                  </CCText>
                  <CCText fontSize={FontSizes[15]}> {class_details}</CCText>
                </Box>
              )}
            </Box>
          ) : (
            <Box style={styles.tableStyle}>
              <Box type="column-flex-start" style={styles.rowFlex}>
                <CCText color={CCColors.Grey.Grey} fontSize={FontSizes[13]}>
                  Batch: {batch_details}
                </CCText>
                {class_details !== '' ? (
                  <CCText color={CCColors.Grey.Grey} fontSize={FontSizes[13]}>
                    Class: {class_details}
                  </CCText>
                ) : (
                  <Spacer spacing={12} />
                )}
              </Box>
            </Box>
          ),
          type: 'text',
        });
        itemValues.push({
          name: isMobileOrTab ? (
            <Box flexDirection="row" style={styles.tableStyle}>
              <CCText fontSize={FontSizes[15]} fontWeight={600}>
                Plan status:{' '}
              </CCText>
              <CCText fontSize={FontSizes[15]}> {status}</CCText>
            </Box>
          ) : (
            <Box style={styles.tableStyle}>
              <Box type="row-flex-start" style={styles.rowFlex}>
                <CCText color={CCColors.Grey.Grey} fontSize={FontSizes[13]}>
                  {status}
                </CCText>
              </Box>
            </Box>
          ),
          type: 'text',
        });
        itemValues.push({
          name: isMobileOrTab ? (
            <Box flexDirection="row" style={styles.tableStyle}>
              <CCText fontSize={FontSizes[15]} fontWeight={600}>
                Start date:{' '}
              </CCText>
              <CCText fontSize={FontSizes[15]}>{start_date}</CCText>
            </Box>
          ) : (
            <Box style={styles.tableStyle}>
              <Box type="row-flex-start" style={styles.rowFlex}>
                <CCText color={CCColors.Grey.Grey} fontSize={FontSizes[13]}>
                  {start_date}
                </CCText>
              </Box>
            </Box>
          ),
          type: 'text',
        });
        itemValues.push({
          name: isMobileOrTab ? (
            <Box flexDirection="row" style={styles.tableStyle}>
              <CCText fontSize={FontSizes[15]} fontWeight={600}>
                End date:{' '}
              </CCText>
              <CCText fontSize={FontSizes[15]}>{end_date}</CCText>
            </Box>
          ) : (
            <Box style={styles.tableStyle}>
              <Box type="row-flex-start" style={styles.rowFlex}>
                <CCText color={CCColors.Grey.Grey} fontSize={FontSizes[13]}>
                  {end_date}
                </CCText>
              </Box>
            </Box>
          ),
          type: 'text',
        });
        itemValues.push({
          name: isMobileOrTab ? (
            <Box flexDirection="row" style={styles.tableStyle}>
              <CCText fontSize={FontSizes[15]} fontWeight={600}>
                Date created:{' '}
              </CCText>
              <CCText fontSize={FontSizes[15]}>{date_created}</CCText>
            </Box>
          ) : (
            <Box style={styles.tableStyle}>
              <Box type="row-flex-start" style={styles.rowFlex}>
                <CCText color={CCColors.Grey.Grey} fontSize={FontSizes[13]}>
                  {date_created}
                </CCText>
              </Box>
            </Box>
          ),
          type: 'text',
        });

        itemValues.push({
          name: isMobileOrTab ? (
            <Box flexDirection="row" style={styles.tableStyle}>
              <CCText fontSize={FontSizes[15]} fontWeight={600}>
                Certificate:{' '}
              </CCText>
              <CCText fontSize={FontSizes[15]}>{date_created}</CCText>
            </Box>
          ) : (
            <Box style={styles.tableStyle}>
              <Box type="row-flex-start" style={styles.rowFlex} onPress={()=>{
                if(certificate){openInNewTab(certificate)}
              }}>
                <CCText color={certificate?CCColors.Blue:CCColors.Grey.Grey} fontSize={FontSizes[13]}>
                  {certificate? 'view':'N/A'}
                </CCText>
              </Box>
            </Box>
          ),
          type: 'text',
        });

        itemValues.push({
          name: isMobileOrTab ? (
            <Box flexDirection="row" style={styles.tableStyle}>
              {normalizedStatus === 'paused' ? (
                // <CCButton
                //   // style={{paddingHorizontal: 16, paddingVertical: 6}}
                //   onPress={() => resumeSubscription({order_id})}
                //   disabled={isResuming}>
                  <Box style={{flexDirection: 'row', alignItems: 'center'}} onPress={() => handleResumeSubscription(order_id)}>
                    <Icon name="play-circle-outline" size={20} color={CCColors.Black} />
                    {/* <CCText fontSize={FontSizes[13]}>&nbsp;Resume</CCText> */}
                  </Box>
                // </CCButton>
              ) : normalizedStatus === 'active'  &&  batch_details?.toLowerCase()?.includes('personal') ? (
                // <CCButton
                //   style={{paddingHorizontal: 16, paddingVertical: 6}}
                //   onPress={() => {
                //     setPauseOrderId(order_id);
                //     setShowPauseModal(true);
                //   }}>
                  <Box
                    style={{flexDirection: 'row', alignItems: 'center'}}
                    onPress={() => {
                      setPauseOrderId(order_id);
                      setPauseEndDate(null);
                      setPauseError('');
                      setPauseLimits(null);
                      setPauseLimitsError('');
                      setSelectedWeeks(1);
                      setShowPauseModal(true);
                      getPauseLimits({order_id});
                    }}>
                    <Icon name="pause-circle-outline" size={20} color={CCColors.Black} />
                    {/* <CCText fontSize={FontSizes[13]}>&nbsp;Pause</CCText> */}
                  </Box>
                // </CCButton>
              ) : (
                <CCText fontSize={FontSizes[13]}>-</CCText>
              )}
            </Box>
          ) : (
            <Box style={styles.tableStyle}>
              <Box type="row-flex-start" style={styles.rowFlex}>
                {normalizedStatus === 'paused' ? (
                  // <CCButton
                  //   style={{paddingHorizontal: 16, paddingVertical: 6}}
                  //   onPress={() => resumeSubscription({order_id})}
                  //   disabled={isResuming}>
                    <Box style={{flexDirection: 'row', alignItems: 'center'}} 
                    onPress={() => handleResumeSubscription(order_id)}
                    >
                      <Icon name="play-circle-outline" size={20} color={CCColors.Black} />
                      {/* <CCText fontSize={FontSizes[13]}>&nbsp;Resume</CCText> */}
                    </Box>
                  // </CCButton>
                ) : normalizedStatus === 'active'  &&  batch_details?.toLowerCase()?.includes('personal') ? (
                  // <CCButton
                  //   style={{paddingHorizontal: 16, paddingVertical: 6}}
                    // onPress={() => {
                    //   setPauseOrderId(order_id);
                    //   setShowPauseModal(true);
                    // }}>
                    <Box
                      style={{flexDirection: 'row', alignItems: 'center'}}
                      onPress={() => {
                        setPauseOrderId(order_id);
                        setPauseEndDate(null);
                        setPauseError('');
                        setPauseLimits(null);
                        setPauseLimitsError('');
                        setShowPauseModal(true);
                        getPauseLimits({order_id});
                      }}>
                      <Icon name="pause-circle-outline" size={20} color={CCColors.Black} />
                      {/* <CCText fontSize={FontSizes[13]}>&nbsp;Pause</CCText> */}
                    </Box>
                  // {/* </CCButton> */}
                ) : (
                  <CCText fontSize={FontSizes[13]}>-</CCText>
                )}
              </Box>
            </Box>
          ),
          type: 'text',
        });

        return itemValues;
      });
      setTableData(formattedData || []);
    }
  }, [subscriptionDetails, currentPage, isMobileOrTab]);

  const cardData = [
    {
      title: 'Live Classes',
      desc: 'Live IM/GM Classes',
      icon: require('@/Assets/Images/Meetings/Instructor.svg'),
    },
    {
      title: 'Coach',
      desc: 'Personalized training',
      icon: require('@/Assets/Images/Meetings/Coach.svg'),
    },
    {
      title: 'Mentor',
      desc: '1-on-1 Mentor Sessions',
      icon: require('@/Assets/Images/Meetings/Mentor.svg'),
    },
    {
      title: 'Practice',
      desc: 'Learn, compete, & grow',
      icon: require('@/Assets/Images/Meetings/Practice.svg'),
    },
  ];

  return (
    <ScrollView style={{padding: 20}}>
      <CCLoader loading={isResuming||isPausing||isLoadingRenewal} bgStyle={{backgroundColor:CCColors.Primary.Light}} />
         {showRenewalBanner && renewalData && (
                <Box paddingHorizontal={isMobileOrTab ? 10 : 20} marginBottom={15}>
                  <RenewalBanner
                    daysLeft={Number(
                      renewalData?.current_subscription?.days_to_expiry || 100,
                    )}
                    couponDiscountPercentage={
                      Number(renewalData?.coupon?.discount_value) ?? 0
                    }
                    expiryDate={renewalData?.current_subscription?.end_date}
                    // daysLeft={1}
                    sessionsLeft={
                      renewalData?.current_subscription?.sessions_left ?? 0
                    }
                    couponCode={renewalData?.coupon?.code}
                    // onRenewNow={() => {
                    //   navigation.navigate(ScreenName.Renewal, {userId});
                    // }}
                    onClose={() => setShowRenewalBanner(false)}
                  />
                </Box>
              )}
      <Spacer spacing={vs(16)} />
     <SlideModal
  visible={showPauseModal}
  type="center"
  modalHeaderTitle="Pause Subscription"
  customStyle={{justifyContent: 'center', alignItems: 'center'}}>
    <CCLoader loading={isLoadingPauseLimits} bgStyle={{backgroundColor: `${CCColors.Primary.Light}80`}} />
  <Box
    style={{
      backgroundColor: CCColors.White,
      borderRadius: 16,
      padding: 20,
      width: isMobileOrTab ? '90%' : 420,
      maxWidth: '100%',
    }}>
    
    {/* Header Icon & Title */}
    <Box style={{alignItems: 'center', marginBottom: 6}}>
      <Box
        style={{
          width: 48,
          height: 48,
          borderRadius: 24,
          backgroundColor: CCColors.Black,
          justifyContent: 'center',
          alignItems: 'center',
          marginBottom: 10,
        }}>
        <Icon name="pause" size={24} color={CCColors.White} />
      </Box>
      <CCText fontSize={FontSizes[18]} fontWeight={600} style={{textAlign: 'center'}}>
        Pause Your Subscription
      </CCText>
      <Spacer spacing={vs(4)} />
      <CCText 
        fontSize={FontSizes[13]} 
        color={CCColors.Grey.Grey}
        style={{textAlign: 'center', lineHeight: 18}}>
        Choose when you'd like to resume your learning journey
      </CCText>
    </Box>

    <Spacer spacing={vs(16)} />

    {/* Pause Information Card */}
    {isLoadingPauseLimits ? (
      <Box
        style={{
          backgroundColor: `${CCColors.Grey.LightGrey}40`,
          borderRadius: 12,
          padding: 14,
          alignItems: 'center',
        }}>
        <CCLoader size="small" />
      </Box>
    ) : pauseLimits ? (
      <Box
        style={{
          backgroundColor: CCColors.White,
          borderRadius: 12,
          padding: 14,
          borderWidth: 1,
          borderColor: CCColors.Grey.LightGrey,
        }}>
        <Box type="row-center-between" style={{marginBottom: 10}}>
          <CCText fontSize={FontSizes[14]} fontWeight={500}>
            Remaining pause days
          </CCText>
          <Box
            style={{
              backgroundColor: remainingPauseDays > 0 ? CCColors.success : CCColors.Error,
              paddingHorizontal: 12,
              paddingVertical: 4,
              borderRadius: 12,
            }}>
            <CCText fontSize={FontSizes[13]} fontWeight={600} color={CCColors.White}>
              {remainingPauseDays} days
            </CCText>
          </Box>
        </Box>
        <Box type="row-center-between">
          <CCText fontSize={FontSizes[13]} color={CCColors.Grey.Grey}>
            Max pause till
          </CCText>
          <CCText fontSize={FontSizes[14]} fontWeight={500}>
            {formatIsoDate(pauseLimits?.max_pause_end_date)}
          </CCText>
        </Box>
      </Box>
    ) : null}

    {pauseLimitsError ? (
      <>
        <Spacer spacing={vs(10)} />
        <Box
          style={{
            backgroundColor: `${CCColors.Error}10`,
            borderRadius: 8,
            padding: 10,
            borderLeftWidth: 3,
            borderLeftColor: CCColors.Error,
          }}>
          <CCText fontSize={FontSizes[12]} color={CCColors.Error}>
            {pauseLimitsError}
          </CCText>
        </Box>
      </>
    ) : null}

    <Spacer spacing={vs(16)} />

    {/* Week Selection Section */}
    <Box>
      <CCText 
        fontSize={FontSizes[14]} 
        fontWeight={500}
        style={{marginBottom: 6}}>
        Select Pause Duration
      </CCText>
      <CCText 
        fontSize={FontSizes[12]} 
        color={CCColors.Grey.Grey}
        style={{marginBottom: 10}}>
        Your subscription will automatically resume on {formatIsoDate(calculatedPauseEndDate)}
      </CCText>
      <Box type="row" style={{gap: 10}}>
        <CCSelect
          ccSelectHeight={45}
          ccSelectWidth="100%"
          placeholder="Select pause duration"
          selectedValue={selectedWeeks}
          onValueChange={(value) => setSelectedWeeks(value)}
          items={weekOptions}
        />
      </Box>
    </Box>

    {pauseError ? (
      <>
        <Spacer spacing={vs(10)} />
        <Box
          style={{
            backgroundColor: `${CCColors.Error}10`,
            borderRadius: 8,
            padding: 10,
            flexDirection: 'row',
            alignItems: 'flex-start',
          }}>
          <Icon name="error" size={16} color={CCColors.Error} style={{marginTop: 1}} />
          <Spacer spacing={vs(6)} horizontal />
          <CCText 
            fontSize={FontSizes[12]} 
            color={CCColors.Error}
            style={{flex: 1}}>
            {pauseError}
          </CCText>
        </Box>
      </>
    ) : null}

    <Spacer spacing={vs(18)} />

    {/* Action Buttons */}
    <Box type="row-center-between" style={{gap: 10}}>
      <CCButton
        style={{
          flex: 1,
          paddingVertical: 12,
          backgroundColor: CCColors.White,
          borderWidth: 1.5,
          borderColor: CCColors.Black,
          borderRadius: 10,
        }}
        onPress={() => {
          setShowPauseModal(false);
          setPauseError('');
          setSelectedWeeks(1);
        }}>
        <CCText fontSize={FontSizes[14]} fontWeight={500} color={CCColors.Black}>
          Cancel
        </CCText>
      </CCButton>
      
      <CCButton
        style={{
          flex: 1,
          paddingVertical: 12,
          backgroundColor: isPausing ? CCColors.Grey.LightGrey : CCColors.YellowDark,
          borderRadius: 10,
          opacity: isPausing ? 0.6 : 1,
        }}
        onPress={() => {
          if (pauseLimits && remainingPauseDays <= 0) {
            setPauseError('Pause limit exhausted for this subscription');
            return;
          }
          const formattedDate = formatPauseEndDate(calculatedPauseEndDate);
          pauseSubscription({
            order_id: pauseOrderId,
            pause_end_date: formattedDate,
            paused_by_student: true,
          });
        }}
        disabled={isPausing}>
        <Box type="row-center" style={{gap: 6}}>
          {isPausing && <CCLoader size="small" color={CCColors.White} />}
          <CCText fontSize={FontSizes[14]} fontWeight={600} color={CCColors.White}>
            {isPausing ? 'Pausing...' : 'Confirm Pause'}
          </CCText>
        </Box>
      </CCButton>
    </Box>
  </Box>
</SlideModal>
      <Box
        type={isMobileOrTab ? 'center' : 'row-center-between'}
        style={{
          marginLeft: isMobileOrTab ? 0 : 30,
        }}>
        <CCText fontSize={24} color={CCColors.Primary.Dark}>
          Subscription Details
        </CCText>
        {isMobileOrTab && <Spacer spacing={10} />}
      </Box>
      <Spacer spacing={vs(20)} />
      <Box
        style={{
          backgroundColor: isMobileOrTab ? CCColors.White : '',
          borderRadius: isMobileOrTab ? 8 : 0,
        }}>
        {tableData?.length > 0 ? (
          <>
            <Box>
              <CCPackageTable
                flexArr={[2, 1.2, 1.2, 2, 1.2, 1.2, 1.2, 1.2, 1.2]}
                headers={tabHeaders}
                tableData={tableData}
                isMargin={false}
              />
            </Box>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              paginate={paginate}
            />
          </>
        ) : (
          <EmptyView mobileNumber={userContact} activeTab={'subscriptions'} />
        )}
      </Box>
    </ScrollView>
  );
};

export default SubscriptionTab;
